import { Webgl2Pipeline } from 'rainbow-web-sdk/services/virtualBackground/pipeline/webgl2/webgl2Pipeline';
import { WebglHelper } from 'rainbow-web-sdk/services/virtualBackground/pipeline/webgl2/webglHelper';

export type BackgroundBlurStage = {
    render(): void
    updateCoverage(coverage: [number, number]): void
    cleanUp(): void
};

export class BlurStage {
    private blur: any;
    private blend: any;

    /**
     * @function create
     * @static
     * @public
     * @param {Options} options The options (type and url) of the image or the video
     * @param {Webgl2Pipeline} vbWebgl2 The instance of the webgl2 class
     * @returns {BlurStage | null} Initialization of the class
     */
    public static create(options: any, vbWebgl2: Webgl2Pipeline): BlurStage {
        if (options.backgroundType === vbWebgl2.VIRTUAL_BACKGROUND_TYPE.IMAGE || options.backgroundType === vbWebgl2.VIRTUAL_BACKGROUND_TYPE.VIDEO) return null;
        return new BlurStage(vbWebgl2, options);
    }

    private constructor(vbWebgl2: Webgl2Pipeline, options: any) {
        this.blur = new BlurPass(vbWebgl2, options);
        this.blend = new BlendPass(vbWebgl2);
    }

    /****************************************************************************/
    /** PUBLIC STUFF                                                           **/
    /****************************************************************************/


    /****************************************************************************/
    /** PRIVATE STUFF                                                          **/
    /****************************************************************************/

    public render() {
        this.blur.render();
        this.blend.render();
    }

    public updateCoverage(coverage: [number, number]) {
        this.blend.updateCoverage(coverage);
    }

    public cleanUp() {
        this.blend.cleanUp();
        this.blur.cleanUp();
    }
}
export class BlurPass {
    private vbHelper: WebglHelper;
    private vbWebgl2: Webgl2Pipeline;
    public program: any;
    private outputWidth: number;
    private outputHeight: number;
    private inputFrameLocation: WebGLUniformLocation;
    private personMaskLocation: WebGLUniformLocation;
    private texelSizeLocation: WebGLUniformLocation;
    private texelHeight: number;
    private texelWidth: number;
    private texture1: WebGLTexture;
    private texture2: WebGLTexture;
    private frameBuffer1: WebGLFramebuffer;
    private frameBuffer2: WebGLFramebuffer;
    private fragmentShaderBlur: WebGLShader;
    private gl: WebGL2RenderingContext;
    private options: any;

    constructor(vbWebgl2: Webgl2Pipeline, options: any) {
        this.vbHelper = WebglHelper.create();
        this.vbWebgl2 = vbWebgl2;
        this.gl = this.vbWebgl2.gl;
        this.options = options;

        const outputWidth: number = this.vbWebgl2.outputCanvasElement.width;
        const outputHeight: number = this.vbWebgl2.outputCanvasElement.height;

        const fragmentShaderSourceBlur = String.raw`#version 300 es
            precision highp float;

            uniform sampler2D u_inputFrame;
            uniform sampler2D u_personMask;
            uniform vec2 u_texelSize;

            in vec2 v_texCoord;

            out vec4 outColor;

            const float offset[5] = float[](0.0, 1.0, 2.0, 3.0, 4.0);
            const float weight[5] = float[](0.2270270270, 0.1945945946, 0.1216216216,
                0.0540540541, 0.0162162162);

            void main(){
                vec4 centerColor = texture(u_inputFrame, v_texCoord);
                float personMask = texture(u_personMask, v_texCoord).a;

                vec4 frameColor = centerColor * weight[0] * (1.0 - personMask);

                for (int i=1; i<5; i++){
                    vec2 offset = vec2(offset[i]) * u_texelSize;

                    vec2 texCoord = v_texCoord + offset;
                    frameColor += texture(u_inputFrame, texCoord) * weight[i] *
                        (1.0 - texture(u_personMask, texCoord).a);

                    texCoord = v_texCoord - offset;
                    frameColor += texture(u_inputFrame, texCoord) * weight[i] *
                        (1.0 - texture(u_personMask, texCoord).a);
                }
                outColor = vec4(frameColor.rgb + (1.0 - frameColor.a) * centerColor.rgb, 1.0);
            }`;

        const fragmentShaderSourceBlurPlus = String.raw`#version 300 es
            precision highp float;

            uniform sampler2D u_inputFrame;
            uniform sampler2D u_personMask;
            uniform vec2 u_texelSize;

            in vec2 v_texCoord;

            out vec4 outColor;

            const float offset[5] = float[](0.0, 3.0, 6.0, 9.0, 12.0);
            const float weight[5] = float[](0.2270270270, 0.1945945946, 0.1216216216,
                0.0540540541, 0.0162162162);

            void main(){
                vec4 centerColor = texture(u_inputFrame, v_texCoord);
                float personMask = texture(u_personMask, v_texCoord).a;

                vec4 frameColor = centerColor * weight[0] * (1.0 - personMask);

                for (int i=1; i<5; i++){
                    vec2 offset = vec2(offset[i]) * u_texelSize;

                    vec2 texCoord = v_texCoord + offset;
                    frameColor += texture(u_inputFrame, texCoord) * weight[i] *
                        (1.0 - texture(u_personMask, texCoord).a);

                    texCoord = v_texCoord - offset;
                    frameColor += texture(u_inputFrame, texCoord) * weight[i] *
                        (1.0 - texture(u_personMask, texCoord).a);
                }
                outColor = vec4(frameColor.rgb + (1.0 - frameColor.a) * centerColor.rgb, 1.0);
            }`;

        const scale = 0.5;
        this.outputWidth = outputWidth * scale;
        this.outputHeight = outputHeight * scale;
        this.texelWidth = 1 / this.outputWidth;
        this.texelHeight = 1 / this.outputHeight;

        if (this.options.backgroundType === vbWebgl2.VIRTUAL_BACKGROUND_TYPE.BLUR) this.fragmentShaderBlur = this.vbHelper.initShader(this.gl, this.gl.FRAGMENT_SHADER, fragmentShaderSourceBlur);
        else if (this.options.backgroundType === vbWebgl2.VIRTUAL_BACKGROUND_TYPE.BLUR_PLUS) this.fragmentShaderBlur = this.vbHelper.initShader(this.gl, this.gl.FRAGMENT_SHADER, fragmentShaderSourceBlurPlus);


        this.program = this.vbHelper.initProgram(this.gl, this.vbWebgl2.vertexShader, this.fragmentShaderBlur, this.vbWebgl2.positionBuffer, this.vbWebgl2.texCoordBuffer);

        this.inputFrameLocation = this.gl.getUniformLocation(this.program, 'u_inputFrame');
        this.personMaskLocation = this.gl.getUniformLocation(this.program, 'u_personMask');
        this.texelSizeLocation = this.gl.getUniformLocation(this.program, 'u_texelSize');
        this.texture1 = this.vbHelper.createTexture(
            this.gl,
            this.gl.RGBA8,
            this.outputWidth,
            this.outputHeight,
            this.gl.NEAREST,
            this.gl.LINEAR
        );

        this.texture2 = this.vbHelper.createTexture(
            this.gl,
            this.gl.RGBA8,
            this.outputWidth,
            this.outputHeight,
            this.gl.NEAREST,
            this.gl.LINEAR
        );

        this.frameBuffer1 = this.gl.createFramebuffer();
        this.gl.bindFramebuffer(this.gl.FRAMEBUFFER, this.frameBuffer1);
        this.gl.framebufferTexture2D(
            this.gl.FRAMEBUFFER,
            this.gl.COLOR_ATTACHMENT0,
            this.gl.TEXTURE_2D,
            this.texture1,
            0
        );

        this.frameBuffer2 = this.gl.createFramebuffer();
        this.gl.bindFramebuffer(this.gl.FRAMEBUFFER, this.frameBuffer2);
        this.gl.framebufferTexture2D(
            this.gl.FRAMEBUFFER,
            this.gl.COLOR_ATTACHMENT0,
            this.gl.TEXTURE_2D,
            this.texture2,
            0
        );

        this.gl.useProgram(this.program);
        this.gl.uniform1i(this.personMaskLocation, 1);
    }

    /**
     * @function render
     * @public
     */
    public render() {

        this.gl.viewport(0, 0, this.outputWidth, this.outputHeight);
        this.gl.useProgram(this.program);
        this.gl.uniform1i(this.inputFrameLocation, 0);
        this.gl.activeTexture(this.gl.TEXTURE1);
        this.gl.bindTexture(this.gl.TEXTURE_2D, this.vbWebgl2.personMaskTexture);

        for (let i = 0; i < 3; i++) {
            this.gl.uniform2f(this.texelSizeLocation, 0, this.texelHeight);
            this.gl.bindFramebuffer(this.gl.FRAMEBUFFER, this.frameBuffer1);
            this.gl.drawArrays(this.gl.TRIANGLE_STRIP, 0, 4);

            this.gl.activeTexture(this.gl.TEXTURE2);
            this.gl.bindTexture(this.gl.TEXTURE_2D, this.texture1);
            this.gl.uniform1i(this.inputFrameLocation, 2);

            this.gl.uniform2f(this.texelSizeLocation, this.texelWidth, 0);
            this.gl.bindFramebuffer(this.gl.FRAMEBUFFER, this.frameBuffer2);
            this.gl.drawArrays(this.gl.TRIANGLE_STRIP, 0, 4);

            this.gl.bindTexture(this.gl.TEXTURE_2D, this.texture2);
        }

    }

    /**
     * @function cleanUp
     * @public
     * @description removes the different components used to make the assembly with the blurring of the environment
     */
    public cleanUp() {
        if (this.frameBuffer1) this.gl.deleteFramebuffer(this.frameBuffer1);
        if (this.frameBuffer2) this.gl.deleteFramebuffer(this.frameBuffer2);
        if (this.texture1) this.gl.deleteTexture(this.texture1);
        if (this.texture2) this.gl.deleteTexture(this.texture2);
        if (this.program) this.gl.deleteProgram(this.program);
        if (this.fragmentShaderBlur) this.gl.deleteShader(this.fragmentShaderBlur);
    }
}
export class BlendPass {

    private vbWebgl2: Webgl2Pipeline;
    private vbHelper: WebglHelper;
    private vertexShaderBlend: WebGLShader;
    private fragmentShaderBlend: WebGLShader;
    private program: WebGLProgram;
    private coverageLocation: WebGLUniformLocation;
    private gl: WebGL2RenderingContext;

    constructor(vbWebgl2: Webgl2Pipeline) {
        this.vbWebgl2 = vbWebgl2;
        this.vbHelper = WebglHelper.create();
        const vertexShaderSourceBlend = String.raw`#version 300 es

        in vec2 a_position;
        in vec2 a_texCoord;

        out vec2 v_texCoord;

        void main(){
            gl_Position = vec4(a_position * vec2(1.0, -1.0), 0.0, 1.0);
            v_texCoord = a_texCoord;
        }`;

        const fragmentShaderSourceBlend = String.raw`#version 300 es
            precision highp float;

            uniform sampler2D u_inputFrame;
            uniform sampler2D u_personMask;
            uniform sampler2D u_blurredInputFrame;
            uniform vec2 u_coverage;

            in vec2 v_texCoord;

            out vec4 outColor;

            void main(){
                vec3 color = texture(u_inputFrame, v_texCoord).rgb;
                vec3 blurredColor = texture(u_blurredInputFrame, v_texCoord).rgb;
                float personMask = texture(u_personMask, v_texCoord).a;
                personMask = smoothstep(u_coverage.x, u_coverage.y, personMask);
                outColor = vec4(mix(blurredColor, color, personMask), 1.0);
            }`;


        this.gl = this.vbWebgl2.gl;

        this.vertexShaderBlend = this.vbHelper.initShader(this.gl, this.gl.VERTEX_SHADER, vertexShaderSourceBlend);
        this.fragmentShaderBlend = this.vbHelper.initShader(this.gl, this.gl.FRAGMENT_SHADER, fragmentShaderSourceBlend);

        this.program = this.vbHelper.initProgram(this.gl, this.vertexShaderBlend, this.fragmentShaderBlend, this.vbWebgl2.positionBuffer, this.vbWebgl2.texCoordBuffer);

        const inputFrameLocation = this.gl.getUniformLocation(this.program, 'u_inputFame');
        const personMaskLocation = this.gl.getUniformLocation(this.program, 'u_personMask');
        const blurredInputFrame = this.gl.getUniformLocation(this.program, 'u_blurredInputFrame');
        this.coverageLocation = this.gl.getUniformLocation(this.program, 'u_coverage');

        this.gl.useProgram(this.program);
        this.gl.uniform1i(inputFrameLocation, 0);
        this.gl.uniform1i(personMaskLocation, 1);
        this.gl.uniform1i(blurredInputFrame, 2);
        this.gl.uniform2f(this.coverageLocation, 0, 1);


    }

    /**
     * @function render
     * @public
     */
    public render() {
        this.gl.viewport(0, 0, this.vbWebgl2.outputCanvasElement.width, this.vbWebgl2.outputCanvasElement.height);

        this.gl.useProgram(this.program);

        this.gl.bindFramebuffer(this.gl.FRAMEBUFFER, null);
        this.gl.drawArrays(this.gl.TRIANGLE_STRIP, 0, 4);
    }

    /**
     * @function updateCoverage
     * @public
     * @param {[number, number]} coverage
     */
    public updateCoverage(coverage: [number, number]) {
        this.gl.useProgram(this.program);
        this.gl.uniform2f(this.coverageLocation, coverage[0], coverage[1]);
    }

    /**
     * @function cleanUp
     * @public
     * @description removes the different components used to make the assembly with the blurring of the environment
     */
    public cleanUp() {
        if (this.program) this.gl.deleteProgram(this.program);
        if (this.fragmentShaderBlend) this.gl.deleteShader(this.fragmentShaderBlend);
        if (this.vertexShaderBlend) this.gl.deleteShader(this.vertexShaderBlend);

    }

}
